import { env } from './env.ts';
import type { DaoCampaign } from '@/types/dao.ts';
// ---------------------------------------------------------------------------
// 문자열 / 숫자 포맷
// ---------------------------------------------------------------------------

/** 숫자만 남긴다. `'010-1234'` -> `'0101234'` */
export function digits(value: string): string {
  return value.replace(/\D/g, '');
}

/** 1234567 -> "1,234,567" */
export function formatPrice(value: number): string {
  return new Intl.NumberFormat('ko-KR').format(value);
}

/** 125 -> "02분 05초" */
export function formatMinSec(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const min = String(Math.floor(seconds / 60)).padStart(2, '0');
  const sec = String(seconds % 60).padStart(2, '0');
  return `${min}분 ${sec}초`;
}

export function isUrl(value: string): boolean {
  return /^(https?:\/\/)[^\s/$.?#].\S*$/i.test(value);
}

/** 한글 안전 base64 인코딩 */
export function encodeBase64(str: string): string {
  const bytes = new TextEncoder().encode(str);
  return btoa(String.fromCharCode(...bytes));
}

/** 한글 안전 base64 디코딩 */
export function decodeBase64(base64: string): string {
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** 암호학적으로 안전한 hex 토큰 (length 는 바이트 수, 결과 길이는 2배) */
export function generateToken(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);

  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** 표시용 랜덤 문자열. 보안 용도면 generateToken 을 쓴다. */
export function randomString(length = 32): string {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const values = crypto.getRandomValues(new Uint8Array(length));

  return Array.from(values, (v) => chars[v % chars.length]).join('');
}

// ---------------------------------------------------------------------------
// 생년월일 검증
// ---------------------------------------------------------------------------

export type BirthValidationOptions = {
  allowFuture?: boolean;
  minAge?: number;
  maxAge?: number;
  today?: Date;
};

export type BirthValidationResult =
  { ok: true; normalized: string; date: Date; age: number } | { ok: false; reason: string };

/** 'YYYYMMDD' 또는 'YYYY-MM-DD' 파싱 */
function parseBirthInput(input: string): { y: number; m: number; d: number } | null {
  const s = input.trim();

  if (/^\d{8}$/.test(s)) {
    return { y: Number(s.slice(0, 4)), m: Number(s.slice(4, 6)), d: Number(s.slice(6, 8)) };
  }

  const matched = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (matched) {
    return { y: Number(matched[1]), m: Number(matched[2]), d: Number(matched[3]) };
  }

  return null;
}

/** 실제 존재하는 날짜인지 (2월 30일 같은 값 걸러냄) */
function isRealDate(y: number, m: number, d: number): boolean {
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return false;
  if (y < 1900 || y > 2100) return false;
  if (m < 1 || m > 12) return false;
  if (d < 1 || d > 31) return false;

  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

function calcAge(birth: Date, today: Date): number {
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age;
}

export function validateBirthDate(
  input: string,
  opts: BirthValidationOptions = {}
): BirthValidationResult {
  const parsed = parseBirthInput(input);
  if (!parsed) {
    return { ok: false, reason: '형식이 올바르지 않습니다. (YYYYMMDD 또는 YYYY-MM-DD)' };
  }

  const { y, m, d } = parsed;
  if (!isRealDate(y, m, d)) return { ok: false, reason: '존재하지 않는 날짜입니다.' };

  const birth = new Date(y, m - 1, d);
  const today = opts.today ?? new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  if (!opts.allowFuture && birth.getTime() > todayStart.getTime()) {
    return { ok: false, reason: '미래 날짜는 입력할 수 없습니다.' };
  }

  const age = calcAge(birth, today);
  if (opts.minAge != null && age < opts.minAge) {
    return { ok: false, reason: `최소 나이는 ${opts.minAge}세입니다.` };
  }
  if (opts.maxAge != null && age > opts.maxAge) {
    return { ok: false, reason: `최대 나이는 ${opts.maxAge}세입니다.` };
  }

  const normalized = `${y}${String(m).padStart(2, '0')}${String(d).padStart(2, '0')}`;
  return { ok: true, normalized, date: birth, age };
}

// ---------------------------------------------------------------------------
// 쿠키
// ---------------------------------------------------------------------------

export interface CookieOptions {
  days?: number;
  path?: string;
  domain?: string;
  sameSite?: 'Lax' | 'Strict' | 'None';
  secure?: boolean;
}

export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  const {
    days,
    path = '/',
    domain,
    sameSite = 'Lax',
    secure = location.protocol === 'https:',
  } = options;

  const parts = [`${encodeURIComponent(name)}=${encodeURIComponent(value)}`];

  if (days !== undefined) {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
    parts.push(`Expires=${expires.toUTCString()}`);
  }

  parts.push(`Path=${path}`);
  if (domain) parts.push(`Domain=${domain}`);
  parts.push(`SameSite=${sameSite}`);
  if (secure) parts.push('Secure');

  document.cookie = parts.join('; ');
}

export function getCookie(name: string): string | null {
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  const prefix = `${encodeURIComponent(name)}=`;

  for (const cookie of cookies) {
    if (cookie.startsWith(prefix)) {
      return decodeURIComponent(cookie.slice(prefix.length));
    }
  }
  return null;
}

export function deleteCookie(name: string, path = '/'): void {
  document.cookie = `${encodeURIComponent(name)}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=${path}`;
}

// ---------------------------------------------------------------------------
// 기타
// ---------------------------------------------------------------------------

/** 현재 경로+쿼리. 로그인 후 되돌아올 주소로 쓴다. */
export function returnUrl(): string {
  return `${location.pathname}${location.search}`;
}

/**
 * 미션 노출 이미지 전부. 관리 화면에 올린 순서(`F_ORDER`) 그대로.
 *
 * 여러 장이면 화면에서 캐러셀로 돈다(`components/image-carousel.tsx`).
 * 한 장도 없으면 빈 배열을 준다 — 부르는 쪽이 대체 이미지를 정한다.
 */
export function campaignImages(campaign: DaoCampaign): string[] {
  return [...(campaign.images ?? [])]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((image) => storageUrl(image.filepath))
    .filter((path): path is string => !!path);
}

/**
 * 미션 대표 이미지 주소. 노출 이미지의 첫 장이다.
 *
 * 서버는 `/urnplay/20260910/....jpg` 처럼 **절대 경로**만 준다. 호스트가 따로면
 * `VITE_UPLOAD_BASE` 를 채운다(비우면 프론트와 같은 오리진 — 구 프로젝트 동작).
 * 이미지가 없는 미션도 있어 대체 이미지를 둔다.
 */
export function campaignThumbnail(campaign: DaoCampaign, fallback = '/images/logo_b.png'): string {
  return campaignImages(campaign)[0] ?? fallback;
}

/**
 * 업로드 이미지가 사는 곳 — `/urnplay/YYYYMMDD/...` (옛 이름 `/musicash/...`).
 *
 * 이 파일들은 **API 서버**에 있다. 프론트는 오리진이 달라서 경로만 그대로 쓰면
 * 프론트에서 찾다가 404 가 나고 화면에 엑박이 뜬다.
 */
const STORAGE_PATH = /^\/(urnplay|musicash)\//;

/**
 * 이미지 주소를 실제로 열리는 곳으로 바꾼다.
 *
 * | 주소                    | 어디에 있나        | 어떻게        |
 * | ----------------------- | ------------------ | ------------- |
 * | `https://...`           | 이미 완전한 주소   | 그대로        |
 * | `/urnplay/...`          | **API 서버**       | API 주소를 붙인다 |
 * | `/images/...` 그 밖에   | 프론트 (public/)   | 그대로        |
 *
 * ★ 전부에 API 주소를 붙이면 안 된다. 씨앗 데이터의 `/images/vol3.jpg` 같은 것은
 *   **프론트 정적 파일**이라 API 서버에는 없다.
 *
 * `VITE_UPLOAD_BASE` 를 비워 두면 API 주소(`VITE_API_BASE`)를 쓴다. 업로드 파일이
 * 별도 호스트로 옮겨가면 그때만 채우면 된다.
 */
export function storageUrl(path?: string | null): string | null {
  if (!path) return null;
  if (/^https?:\/\//.test(path)) return path;
  if (!STORAGE_PATH.test(path)) return path;

  return `${env.uploadBase || env.apiBase}${path}`;
}

/**
 * 다른 서비스에 넘길 절대 주소.
 *
 * 서버는 이미지를 `/musicash/...` 처럼 절대 **경로**로만 준다. 공유 카드(카카오
 * 피드의 큰 그림)는 다른 서비스가 직접 가져가므로 호스트까지 붙어 있어야 한다.
 * `VITE_UPLOAD_BASE` 가 비어 있으면 프론트와 같은 오리진이다.
 */
export function absoluteUrl(path?: string | null): string | undefined {
  if (!path) return undefined;
  if (/^https?:\/\//.test(path)) return path;

  // 업로드 파일은 API 서버에 있다. 나머지는 프론트에 있다.
  const resolved = storageUrl(path);
  if (resolved && /^https?:\/\//.test(resolved)) return resolved;

  const base = window.location.origin;
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
}

/**
 * 밖으로 내보낼 페이지 주소.
 *
 * 지금 보고 있는 `location.href` 를 그대로 공유하면 안 된다.
 * - `?r=` 같은 그때뿐인 질의문자열이 함께 나간다.
 * - 개발 중에는 `dev.urnplay.com` 이 나가는데, 그 이름은 **개발 PC 의 hosts 에만**
 *   있어서 카카오톡으로 받은 사람이 눌러도 열리지 않는다.
 *
 * `VITE_SHARE_BASE` 를 채우면 그 호스트로 내보낸다. 비우면 지금 오리진을 쓴다.
 */
export function shareUrl(path: string): string {
  const base = env.shareBase || window.location.origin;
  return `${base.replace(/\/$/, '')}${path.startsWith('/') ? '' : '/'}${path}`;
}
